import { useState } from "react";
import { HelpCircle, Plus, Sliders } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { PackageCard } from "@/components/package-card";
import { CustomPackageBuilderModal } from "@/components/custom-package-builder-modal";
import { CustomPackageCard } from "@/components/custom-package-card";
import { type PackageTier } from "@/lib/plan-storage";
import { usePlanStore } from "@/stores/plan-store";
import { useAuth } from "@/lib/auth";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import type { PricingSectionProps } from "./types";

export function PricingSection({
  user,
  sponsorableShowsCount,
  contextName,
  pricing,
  isCalculating,
  selectedCount,
  packageCounts,
  onAddPackage,
  pricingCardsRef,
}: PricingSectionProps) {
  const { user: authUser } = useAuth();
  const { isPricingVisible } = usePricingVisibility();
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);

  // Get custom package from store - direct state access for better reactivity
  const customPackage = usePlanStore(state => {
    const userKey = authUser?.id ?? 0;
    return state.customPackages[userKey] || null;
  });
  const hasCustomPackage = customPackage !== null && (customPackage?.selectedDeliverables?.length ?? 0) > 0;
  const setCustomPackageDeliverables = usePlanStore(state => state.setCustomPackageDeliverables);
  const clearCustomPackage = usePlanStore(state => state.clearCustomPackage);

  if (!user || sponsorableShowsCount === 0) return null;

  // Determine show counts based on pricing structure
  const setCoreShowCount = pricing?.setCoreShowCount ?? pricing?.showCount ?? 0;
  const setAudioShowCount = pricing?.setAudioShowCount ?? pricing?.showCount ?? 0;
  const setVideoShowCount = pricing?.setVideoShowCount ?? pricing?.showCount ?? 0;

  const handleSaveCustomPackage = (deliverables: string[], basePackageTier: 'set-core' | 'set-audio' | 'set-video') => {
    setCustomPackageDeliverables(deliverables, basePackageTier, authUser?.id);
  };

  const handleClearCustomPackage = () => {
    clearCustomPackage(authUser?.id);
  };

  const handleAddCustomToProposal = () => {
    onAddPackage('custom', 0); // Custom packages have no calculated price
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <h2 className="text-3xl font-archivo font-black text-max-primary tracking-tight">
            MAXtimate™ Package Pricing
          </h2>
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-max-primary/70 hover:text-max-primary transition-colors">
                <HelpCircle className="w-5 h-5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-semibold text-max-primary">MAXtimate™ Package Pricing</h4>
                <p className="text-sm text-muted-foreground">
                  {pricing ? (
                    <>Package-specific pricing based on shows committed and selected {contextName}.</>
                  ) : (
                    <>Select shows below to see dynamic pricing calculations {contextName}.</>
                  )}
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• SET Core: Essential sponsorship foundation</li>
                  <li>• SET Audio: Audio-enhanced brand experience</li>
                  <li>• SET Video: Video-powered premium partnership</li>
                  {pricing && (
                    <li className="font-medium text-gray-700">• Each package priced based on its own show count (committed + selected)</li>
                  )}
                  {pricing && (setCoreShowCount > 1 || setAudioShowCount > 1 || setVideoShowCount > 1) && (
                    <li>• Volume pricing applied per package tier</li>
                  )}
                </ul>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {pricing ? (
          <p className="text-muted-foreground">
            Package-specific pricing shown below
            {(setCoreShowCount > 1 || setAudioShowCount > 1 || setVideoShowCount > 1) && (
              <span className="text-green-600 font-medium ml-1">
                (Multi-show volume pricing included)
              </span>
            )}
            {isCalculating && (
              <span className="text-blue-600 font-medium ml-1">
                (Calculating...)
              </span>
            )}
          </p>
        ) : (
          <div className="text-muted-foreground">
            <p className="mb-1">Select one or more shows below to see dynamic pricing calculations</p>
            <p className="text-sm text-gray-500">Multi-show volume pricing available</p>
          </div>
        )}
      </div>

      {/* Pricing Tiers Grid */}
      <div ref={pricingCardsRef} className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto mt-8">
        <PackageCard
          packageType="set-core"
          price={pricing?.setCore || 0}
          pricePerShow={setCoreShowCount > 0 ? Math.round((pricing?.setCore || 0) / setCoreShowCount) : 0}
          selectedShowCount={packageCounts['set-core'].total}
          onAddToProposal={onAddPackage}
          disabled={!user || selectedCount === 0 || !pricing}
          isCalculating={isCalculating}
          showPricing={isPricingVisible}
        />

        <PackageCard
          packageType="set-audio"
          price={pricing?.setAudio || 0}
          pricePerShow={setAudioShowCount > 0 ? Math.round((pricing?.setAudio || 0) / setAudioShowCount) : 0}
          selectedShowCount={packageCounts['set-audio'].total}
          onAddToProposal={onAddPackage}
          disabled={!user || selectedCount === 0 || !pricing}
          isCalculating={isCalculating}
          showPricing={isPricingVisible}
        />

        <PackageCard
          packageType="set-video"
          price={pricing?.setVideo || 0}
          pricePerShow={setVideoShowCount > 0 ? Math.round((pricing?.setVideo || 0) / setVideoShowCount) : 0}
          selectedShowCount={packageCounts['set-video'].total}
          onAddToProposal={onAddPackage}
          disabled={!user || selectedCount === 0 || !pricing}
          isCalculating={isCalculating}
          showPricing={isPricingVisible}
        />
      </div>

      {/* Custom Package Section */}
      {hasCustomPackage && customPackage ? (
        <div className="mt-8 max-w-4xl mx-auto">
          <CustomPackageCard
            selectedDeliverables={customPackage.selectedDeliverables}
            selectedShowCount={selectedCount}
            basePackageTier={customPackage.basePackageTier}
            onEdit={() => setIsCustomModalOpen(true)}
            onAddToProposal={handleAddCustomToProposal}
            onClear={handleClearCustomPackage}
            disabled={!user || selectedCount === 0}
          />
        </div>
      ) : (
        <div className="mt-6 pt-4 border-t text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => setIsCustomModalOpen(true)}
            className="border-2 border-green-600 text-green-600 hover:bg-green-50 hover:border-green-700"
          >
            <Sliders className="w-5 h-5 mr-2" />
            Create Custom Package
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Customize your sponsorship package with à la carte add-ons
          </p>
        </div>
      )}

      {/* Custom Package Builder Modal */}
      <CustomPackageBuilderModal
        isOpen={isCustomModalOpen}
        onClose={() => setIsCustomModalOpen(false)}
        onSave={handleSaveCustomPackage}
        initialDeliverables={customPackage?.selectedDeliverables || []}
        initialBasePackageTier={customPackage?.basePackageTier || 'set-core'}
      />
    </div>
  );
}
